import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Skeleton,
  Typography,
} from '@mui/material';
import React, { useLayoutEffect, useRef, useState, useMemo } from 'react';

const columns = [
  {
    group: 'Frontline and DD Pro Mapping',
    fields: [
      { key: 'ff_dealer', label: 'Frontline Dealer(s)' },
      { key: 'pro_dd_dealer', label: 'DD Pro Dealer(s)' },
    ],
  },
  {
    group: 'Frontline',
    fields: [
      {
        key: 'usedVehiclesWithoutPhotos',
        label: 'Used Missing Photos',
      },
      { key: 'usedVehicleCount', label: 'Used Total' },
      {
        key: 'used_percent',
        label: 'Used Missing %',
        type: 'percentage',
      },
    ],
  },
  {
    group: 'DD Pro',
    fields: [
      { key: 'uc_details_in_queue', label: 'UC Details In Queue' },
      { key: 'uc_details_completed', label: 'UC Details Completed' },
    ],
  },
  {
    group: 'Frontline',
    fields: [
      { key: 'newVehiclesWithoutPhotos', label: 'New Missing Photos' },
      { key: 'newVehicleCount', label: 'New Total' },
      {
        key: 'new_percent',
        label: 'New Missing %',
        type: 'percentage',
      },
      {
        key: 'usedVehiclesCompletedYesterday',
        label: 'UC Photos Completed',
      },
      {
        key: 'newVehiclesCompletedYesterday',
        label: 'NC Photos Completed',
      },
    ],
  },
  {
    group: 'DD Pro',
    fields: [
      {
        key: 'nc_lot_preps_completed',
        label: 'NC Lot Preps Completed',
      },
      {
        key: 'nc_deliveries_completed',
        label: 'NC Deliveries Completed',
      },
    ],
  },
];

const SkeletonRow = ({ fieldMeta }) => {
  return (
    <TableRow>
      {fieldMeta.map(field => (
        <TableCell key={field.key}>
          <Skeleton variant="text" width="80%" height={30} />
        </TableCell>
      ))}
    </TableRow>
  );
};

const useFieldMeta = () => {
  return useMemo(() => {
    let colIndex = 0;

    return columns.flatMap(group =>
      group.fields.map((field, fIndex) => {
        let leftOffset;
        let isSticky = false;

        if (colIndex === 0) {
          leftOffset = 0;
          isSticky = true;
        } else if (colIndex === 1) {
          leftOffset = 100;
          isSticky = true;
        } else {
          leftOffset = undefined;
        }

        const meta = {
          ...field,
          index: colIndex,
          isGroupEnd: fIndex === group.fields.length - 1,
          leftOffset,
          isSticky,
        };

        colIndex++;
        return meta;
      })
    );
  }, []);
};

const formatPercentage = value => {
  if (typeof value === 'string' && value.includes('%')) {
    return Number.parseFloat(value) === 0 ? '0%' : value;
  }

  if (value === undefined || value === null) {
    return value;
  }

  const num = Number(value);
  if (Number.isNaN(num)) {
    return value;
  }

  return num === 0 ? '0%' : `${num.toFixed(2)}%`;
};

const formatValue = (value, field) => {
  if (field.type === 'percentage') {
    return formatPercentage(value);
  }

  if (typeof value === 'string') {
    const parts = value.split(',');
    if (parts.length > 1) {
      return parts.map(i => (
        <Box key={i} sx={{ mb: 2 }}>
          {i.trim()}
        </Box>
      ));
    }
  }

  return value;
};

const MultiHeaderTable = ({
  data = [],
  summaryData = [],
  loading = false,
  error = null,
}) => {
  const fieldMeta = useFieldMeta();

  // Measure the rendered width of col 0 so col 1's sticky left is always exact.
  // useLayoutEffect fires before paint so there's no visible jump.
  const col1Ref = useRef(null);
  const [col2Left, setCol2Left] = useState(100);

  useLayoutEffect(() => {
    const el = col1Ref.current;
    let cleanup;
    if (el) {
      const measure = () => setCol2Left(el.offsetWidth);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      cleanup = () => observer.disconnect();
    }
    return cleanup;
  }, []);

  const getStickyLeft = field => {
    if (!field.isSticky) return undefined;
    return field.index === 1 ? col2Left : 0;
  };

  const renderTableBody = () => {
    if (loading) {
      return Array.from({ length: 5 }).map((_, i) => (
        <SkeletonRow key={`skeleton-row-${i}`} fieldMeta={fieldMeta} />
      ));
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={fieldMeta.length} align="center">
            <Typography color="error">Failed to load data.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    if (data.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={fieldMeta.length} align="center">
            No data available
          </TableCell>
        </TableRow>
      );
    }

    return data.map((row, index) => {
      const rowKey =
        row.ff_dealer_id || row.ddpro_dealer_ids || row.pro_dd_dealer || index;

      return (
        <TableRow key={`${rowKey}-${index}`} hover>
          {fieldMeta.map(field => (
            <TableCell
              key={field.key}
              align="center"
              sx={{
                borderRight: field.isGroupEnd
                  ? '2px solid #e0e0e0'
                  : '1px solid #f5f5f5',
                position: field.isSticky ? 'sticky' : 'static',
                left: getStickyLeft(field),
                backgroundColor: field.isSticky ? '#fff' : 'inherit',
                zIndex: field.isSticky ? 2 : 0,
              }}
            >
              {formatValue(row[field.key], field)}
            </TableCell>
          ))}
        </TableRow>
      );
    });
  };

  const renderSummaryRow = () => {
    if (loading) {
      return <SkeletonRow fieldMeta={fieldMeta} />;
    }

    if (error) {
      return null;
    }

    const formattedSummaryData = [
      '',
      'Total',
      summaryData.used_photos || 0,
      summaryData.used_veh || 0,
      summaryData.used_percent || '0%',
      summaryData.uc_det_queue || 0,
      summaryData.uc_det_comp || 0,
      summaryData.new_photos || 0,
      summaryData.new_veh || 0,
      summaryData.new_percent || '0%',
      summaryData.used_comp_yest || 0,
      summaryData.new_comp_yest || 0,
      summaryData.nc_preps_comp || 0,
      summaryData.nc_del_comp || 0,
    ];

    return (
      <TableRow key={'summary-row'} hover>
        {fieldMeta.map((field, index) => (
          <TableCell
            key={field.key}
            align="center"
            sx={{
              borderRight: field.isGroupEnd
                ? '2px solid #e0e0e0'
                : '1px solid #f5f5f5',
              position: field.isSticky ? 'sticky' : 'static',
              left: getStickyLeft(field),
              backgroundColor: field.isSticky ? '#fff' : 'inherit',
              zIndex: field.isSticky ? 2 : 0,
            }}
          >
            {formatValue(formattedSummaryData[index], field)}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  const bodyRows = renderTableBody();
  const summaryRow = renderSummaryRow();

  return (
    <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 350px)' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            {columns.map((group, i) => (
              <TableCell
                key={group.group + i}
                align="center"
                colSpan={group.fields.length}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: '#fafafa',
                  borderBottom: '2px solid #ccc',

                  position: 'sticky',
                  top: 0,
                  // Corner cell (first group covers the 2 frozen columns) must also
                  // stick left so it stays in sync with frozen columns on horizontal scroll
                  ...(i === 0 && { left: 0 }),
                  zIndex: i === 0 ? 6 : 4,

                  borderRight:
                    i === columns.length - 1 ? 'none' : '2px solid #e0e0e0',
                }}
              >
                {group.group}
              </TableCell>
            ))}
          </TableRow>

          <TableRow>
            {fieldMeta.map(field => (
              <TableCell
                ref={field.index === 0 ? col1Ref : undefined}
                key={field.key}
                align="center"
                sx={{
                  fontWeight: 500,
                  whiteSpace: 'nowrap',
                  backgroundColor: '#fff',
                  position: 'sticky',
                  top: 37,
                  // Frozen column header cells must stick left too (corner behaviour)
                  ...(field.isSticky && { left: getStickyLeft(field) }),
                  zIndex: field.isSticky ? 5 : 3,

                  borderRight: field.isGroupEnd
                    ? '2px solid #e0e0e0'
                    : '1px solid #f0f0f0',
                }}
              >
                {field.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {bodyRows}
          {summaryRow}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MultiHeaderTable;
